import { useState, useCallback, useRef } from 'react';
import { ChatSession, ChatMessage, AIExpert } from '@garbera/shared';
import { ChatError } from '../../types/message';
import { ChatSessionState } from './types';
import axios from 'axios';

// 拡張されたChatSessionインターフェース（APIレスポンス用）
interface ExtendedChatSession extends ChatSession {
  expert?: AIExpert;
  isActive?: boolean;
  messageCount?: number;
}

/**
 * チャットセッション管理を担当するフック
 */
export const useChatSession = () => {
  const [state, setState] = useState<ChatSessionState>({
    session: null,
    isLoading: false,
    error: null,
  });

  const currentExpertRef = useRef<AIExpert | null>(null);

  const startNewSession = useCallback(async (expert: AIExpert): Promise<ChatSession | null> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      currentExpertRef.current = expert;

      // セッション作成のAPI呼び出し
      const response = await axios.post('/api/chat/sessions', {
        expertId: expert.id,
        title: `${expert.name}との相談` // デフォルトタイトル
      });

      // レスポンス構造の安全性チェック
      if (response.data?.success && response.data?.data?.session) {
        const sessionData = response.data.data.session;
        
        // 必須フィールドの存在チェック
        if (!sessionData.id || !sessionData.userId || !sessionData.expertId) {
          throw new Error('サーバーから不完全なセッションデータを受信しました');
        }

        const newSession: ExtendedChatSession = {
          id: sessionData.id,
          userId: sessionData.userId,
          expertId: sessionData.expertId,
          title: sessionData.title || `${expert.name}との相談`,
          createdAt: sessionData.createdAt,
          updatedAt: sessionData.updatedAt,
          // 拡張フィールド
          expert: sessionData.expert || expert,
          isActive: sessionData.isActive !== undefined ? sessionData.isActive : true,
          messageCount: sessionData.messageCount || 0,
        };

        setState(prev => ({
          ...prev,
          session: newSession,
          isLoading: false,
        }));

        return newSession;
      } else {
        const errorMessage = response.data?.error?.message || 'セッションの作成に失敗しました';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = 'セッションの開始に失敗しました';
      
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.error?.message || 'サーバーエラーが発生しました';
      } else if (typeof error === 'object' && error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        error: {
          type: 'network',
          message: errorMessage,
          details: error,
        },
        isLoading: false,
      }));
      return null;
    }
  }, []);

  const loadChatHistory = useCallback(async (sessionId: number): Promise<{
    session: ChatSession | null;
    messages: ChatMessage[];
  }> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // セッション情報とメッセージ履歴を並行して取得
      const [sessionResponse, messagesResponse] = await Promise.all([
        axios.get(`/api/chat/sessions/${sessionId}`),
        axios.get(`/api/chat/sessions/${sessionId}/messages?limit=100&order=ASC`)
      ]);

      // セッションレスポンスの安全性チェック
      if (sessionResponse.data?.success && sessionResponse.data?.data?.session) {
        const sessionData = sessionResponse.data.data.session;
        
        // 必須フィールドの存在チェック
        if (!sessionData.id || !sessionData.userId || !sessionData.expertId) {
          throw new Error('サーバーから不完全なセッションデータを受信しました');
        }

        // メッセージレスポンスの安全性チェック
        if (!messagesResponse.data?.success) {
          throw new Error('メッセージの取得に失敗しました');
        }

        const messagesData = messagesResponse.data.data?.messages || [];

        const loadedSession: ExtendedChatSession = {
          id: sessionData.id,
          userId: sessionData.userId,
          expertId: sessionData.expertId,
          title: sessionData.title || '無題のセッション',
          createdAt: sessionData.createdAt,
          updatedAt: sessionData.updatedAt,
          // 拡張フィールド
          expert: sessionData.expert,
          isActive: sessionData.isActive !== undefined ? sessionData.isActive : true,
          messageCount: sessionData.messageCount || 0,
        };

        // 現在の専門家を設定
        if (sessionData.expert) {
          currentExpertRef.current = sessionData.expert;
        }

        setState(prev => ({
          ...prev,
          session: loadedSession,
          isLoading: false,
        }));

        return {
          session: loadedSession,
          messages: messagesData,
        };
      } else {
        const errorMessage = sessionResponse.data?.error?.message || 'セッションの取得に失敗しました';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = 'チャット履歴の読み込みに失敗しました';
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          errorMessage = 'セッションが見つかりません';
        } else {
          errorMessage = error.response?.data?.error?.message || 'サーバーエラーが発生しました';
        }
      } else if (typeof error === 'object' && error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        error: {
          type: 'network',
          message: errorMessage,
          details: error,
        },
        isLoading: false,
      }));

      return { session: null, messages: [] };
    }
  }, []);

  const endSession = useCallback(async (sessionId: number): Promise<boolean> => {
    try {
      // セッション終了のAPI呼び出し（現在はローカル状態をクリアするだけ）
      setState(prev => ({
        ...prev,
        session: null,
      }));
      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: {
          type: 'network',
          message: 'セッションの終了に失敗しました',
          details: error,
        },
      }));
      return false;
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    currentExpert: currentExpertRef.current,
    startNewSession,
    loadChatHistory,
    endSession,
    clearError,
  };
};